// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import BasicSearchPage from '@/views/searches/BasicSearchPage.vue'
// Lazy-loaded below in route definition
import { useAuthStore } from '@/stores/authStore'
import { useAdminStore } from '@/stores/adminStore'
import i18n, { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '@/i18n'

// ---------------------------------------------------------------------------
// localePath — preserve the current locale prefix when building internal links
// Usage: localePath('/explore') → '/pl/explore' (if locale is pl)
//        localePath('/explore') → '/explore'     (if locale is en)
// ---------------------------------------------------------------------------
export function localePath(path, locale) {
  const loc = locale || i18n.global.locale.value
  if (SUPPORTED_LOCALES.includes(loc)) {
    return `/${loc}${path === '/' ? '' : path}`
  }
  return path
}

// ---------------------------------------------------------------------------
// Locale-aware content routes (wrapped under optional /:locale(pl)? prefix)
// ---------------------------------------------------------------------------
const localePrefix = '/:locale(pl)?'

const contentRoutes = [
  {
    path: '/',
    name: 'Home',
    component: BasicSearchPage,
    meta: {
      requiresAuth: false,
      titleKey: 'meta.home.title',
      descriptionKey: 'meta.home.description',
    },
  },
  {
    path: '/search/results/:hash',
    name: 'Results',
    component: () => import('@/views/searches/NewResultsPage.vue'),
    props: true,
    meta: {
      requiresAuth: false,
      titleKey: 'meta.results.title',
      descriptionKey: 'meta.results.description',
      // Result permalinks are per-search and transient — no search value.
      noindex: true,
    },
  },
  {
    path: '/explore',
    name: 'Explore',
    component: () => import('@/views/ExplorePage.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.explore.title',
      descriptionKey: 'meta.explore.description',
    },
  },
  // --- Flight Ideas / SEO landing pages ---
  {
    path: '/flight-ideas',
    name: 'FlightIdeas',
    component: () => import('@/views/ideas/FlightIdeasHub.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.flightIdeas.title',
      descriptionKey: 'meta.flightIdeas.description',
    },
  },
  {
    path: '/flights-from/:city/:filter?',
    name: 'FlightsFrom',
    component: () => import('@/views/ideas/FlightsFromPage.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.flightsFrom.title',
      descriptionKey: 'meta.flightsFrom.description',
    },
  },
  {
    path: '/meet-in/:cityA/:cityB',
    name: 'MeetIn',
    component: () => import('@/views/ideas/MeetInPage.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.meetIn.title',
      descriptionKey: 'meta.meetIn.description',
    },
  },
  // Dashboard hub - shows all preview widgets
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/dashboard/DashboardPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Dashboard - ProperFlight',
      description: 'Your ProperFlight dashboard. View search history, saved flights, shared flights, alerts, and settings at a glance.'
    },
  },
  // Add a placeholder route for profile
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/dashboard/ProfilePage.vue'),
    meta: { 
      requiresAuth: true,
      title: 'My Profile - ProperFlight',
      description: 'Manage your ProperFlight profile. View your search history, saved flights, and personal preferences.'
    },
  },
  // Search History full page
  {
    path: '/search-history',
    name: 'SearchHistory',
    component: () => import('@/views/dashboard/SearchHistoryPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Search History - ProperFlight',
      description: 'View and manage your complete flight search history on ProperFlight.'
    },
  },
  // Search Settings full page
  {
    path: '/search-settings',
    name: 'SearchSettings',
    component: () => import('@/views/dashboard/SearchSettingsPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Search Settings - ProperFlight',
      description: 'Customize your default search preferences on ProperFlight.'
    },
  },
  // New routes for navigation bar items
  {
    path: '/searches/basic',
    name: 'BasicSearch',
    component: BasicSearchPage,
    meta: {
      requiresAuth: false,
      titleKey: 'meta.basicSearch.title',
      descriptionKey: 'meta.basicSearch.description',
    },
  },
  {
    path: '/weather',
    name: 'WeatherSearchPage',
    redirect: (to) => localePath('/searches/weather', to.params.locale)
  },
  {
    path: '/buddy',
    redirect: (to) => localePath('/searches/buddy', to.params.locale)
  },
  {
    path: '/airport-review',
    name: 'AirportReview',
    component: () => import('@/views/searches/AirportReviewPage.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.airportReview.title',
      descriptionKey: 'meta.airportReview.description',
    },
  },
  {
    path: '/searches/weather',
    name: 'WeatherSearch',
    component: BasicSearchPage,
    meta: {
      requiresAuth: false,
      titleKey: 'meta.weatherSearch.title',
      descriptionKey: 'meta.weatherSearch.description',
    },
  },
  {
    path: '/searches/buddy',
    name: 'BuddySearch',
    component: BasicSearchPage,
    meta: {
      requiresAuth: false,
      titleKey: 'meta.buddySearch.title',
      descriptionKey: 'meta.buddySearch.description',
    },
  },
  {
    path: '/shared',
    name: 'Shared',
    component: () => import('@/views/dashboard/SharedPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Shared Flights - ProperFlight',
      description: 'View shared flights from friends and travel groups on ProperFlight. Collaborate on travel planning and discover new destinations.'
    },
  },
  {
    path: '/saved',
    name: 'Saved',
    component: () => import('@/views/dashboard/SavedPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Saved Flights - ProperFlight',
      description: 'Manage your saved flights on ProperFlight. Keep track of favorite routes and prices for future travel planning.'
    },
  },
  {
    path: '/visited',
    name: 'Visited',
    component: () => import('@/views/dashboard/VisitedPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Visited Flights - ProperFlight',
      description: 'View your flight history on ProperFlight. Track past trips and revisit favorite destinations.'
    },
  },
  {
    path: '/planned',
    name: 'Planned',
    component: () => import('@/views/dashboard/PlannedPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Planned Flights - ProperFlight',
      description: 'Manage your upcoming travel plans on ProperFlight. Organize future flights and travel itineraries.'
    },
  },
  {
    path: '/alerts',
    name: 'Alerts',
    component: () => import('@/views/dashboard/AlertsPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Flight Alerts - ProperFlight',
      description: 'Set up flight price alerts on ProperFlight. Get notified when flight prices drop for your favorite routes.'
    },
  },
  {
    path: '/searches/buddy/results',
    name: 'BuddyResults',
    component: () => import('@/views/searches/BuddyResultsPage.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.buddyResults.title',
      descriptionKey: 'meta.buddyResults.description',
      // Transient, query-driven results — no search value.
      noindex: true,
    },
  },
  // --- Auth flow pages (password reset + email verification) ---
  {
    path: '/auth/password-reset',
    name: 'PasswordResetRequest',
    component: () => import('@/views/auth/PasswordResetRequestPage.vue'),
    meta: {
      requiresAuth: false,
      title: 'Reset your password - ProperFlight',
      description: 'Request a password reset email for your ProperFlight account.'
    },
  },
  {
    path: '/auth/password-reset/:key?',
    name: 'PasswordResetConfirm',
    component: () => import('@/views/auth/PasswordResetConfirmPage.vue'),
    props: true,
    meta: {
      requiresAuth: false,
      secretInPath: true,
      title: 'Choose a new password - ProperFlight',
      description: 'Set a new password for your ProperFlight account.'
    },
  },
  {
    path: '/auth/verify-email/:key?',
    name: 'VerifyEmail',
    component: () => import('@/views/auth/VerifyEmailPage.vue'),
    props: true,
    meta: {
      requiresAuth: false,
      secretInPath: true,
      title: 'Verify your email - ProperFlight',
      description: 'Confirm your email address for your ProperFlight account.'
    },
  },
  // --- Legal / compliance pages ---
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/legal/PrivacyPolicyPage.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.privacy.title',
      descriptionKey: 'meta.privacy.description',
    },
  },
  {
    path: '/cookies',
    name: 'CookiePolicy',
    component: () => import('@/views/legal/CookiePolicyPage.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.cookies.title',
      descriptionKey: 'meta.cookies.description',
    },
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('@/views/legal/TermsPage.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.terms.title',
      descriptionKey: 'meta.terms.description',
    },
  },
  // About redirect → homepage
  { path: '/about', redirect: '/' },
  // /faq removed – FAQ is now part of the homepage
  { path: '/faq', redirect: '/' },
]

// Build locale-prefixed versions of content routes
function buildLocaleRoutes(routes, prefix) {
  return routes.map((route) => {
    const built = {
      ...route,
      path: prefix + route.path,
      name: route.name ? `${route.name}Locale` : undefined,
    }
    if (route.redirect) {
      built.redirect = typeof route.redirect === 'function'
        ? route.redirect
        : prefix + (route.redirect === '/' ? '' : route.redirect)
      delete built.component
    } else {
      delete built.redirect
    }
    return built
  })
}

const routes = [
  // ---------------------------------------------------------------------------
  // Locale-prefixed routes (e.g. /pl/explore)
  // ---------------------------------------------------------------------------
  ...buildLocaleRoutes(contentRoutes, localePrefix),

  // ---------------------------------------------------------------------------
  // English root routes (no prefix)
  // ---------------------------------------------------------------------------
  ...contentRoutes,

  // ---------------------------------------------------------------------------
  // Dev/admin-only routes — English only, no locale prefix
  // ---------------------------------------------------------------------------
  // Debug page - only for local development
  {
    path: '/debug',
    name: 'Debug',
    component: () => import('@/views/admin/DebugPage.vue'),
    meta: {
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      // Only allow in development (localhost or 127.0.0.1)
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      if (isDev) {
        next()
      } else {
        next({ name: 'NotFound' })
      }
    }
  },
  // Buddy search debug page - local only
  {
    path: '/debug/buddy',
    name: 'BuddyDebug',
    component: () => import('@/views/admin/BuddyDebugPage.vue'),
    meta: {
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      if (isDev) {
        next()
      } else {
        next({ name: 'NotFound' })
      }
    }
  },
  // Image maintenance page - local only
  {
    path: '/images',
    name: 'ImageMaintenance',
    component: () => import('@/views/admin/ImageMaintenancePage.vue'),
    meta: {
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      // Only allow in development (localhost or 127.0.0.1)
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      if (isDev) {
        next()
      } else {
        next({ name: 'NotFound' })
      }
    }
  },
  // Airport maintenance page - local only
  {
    path: '/airports',
    name: 'AirportMaintenance',
    component: () => import('@/views/admin/AirportMaintenancePage.vue'),
    meta: {
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      // Only allow in development (localhost or 127.0.0.1)
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      if (isDev) {
        next()
      } else {
        next({ name: 'NotFound' })
      }
    }
  },
  // Ad preview page - local only
  {
    path: '/ad-preview',
    name: 'AdPreview',
    component: () => import('@/views/admin/AdPreviewPage.vue'),
    meta: {
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      // Only allow in development (localhost or 127.0.0.1)
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      if (isDev) {
        next()
      } else {
        next({ name: 'NotFound' })
      }
    }
  },
  // Results ad preview page - local only
  {
    path: '/ad-preview-results',
    name: 'AdPreviewResults',
    component: () => import('@/views/admin/AdPreviewResultsPage.vue'),
    meta: {
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      // Only allow in development (localhost or 127.0.0.1)
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      if (isDev) {
        next()
      } else {
        next({ name: 'NotFound' })
      }
    }
  },
  // ------------------------------------------------------------------
  // Superadmin analytics (requires admin session token)
  // ------------------------------------------------------------------
  {
    path: '/superadmin/login',
    name: 'SuperAdminLogin',
    component: () => import('@/views/admin/SuperAdminLoginPage.vue'),
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      title: 'Admin Login - ProperFlight',
    },
  },
  {
    path: '/superadmin/verify',
    name: 'SuperAdminVerify',
    component: () => import('@/views/admin/SuperAdminVerifyPage.vue'),
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      title: 'Verifying admin access - ProperFlight',
    },
  },
  {
    path: '/superadmin',
    name: 'SuperAdminDashboard',
    component: () => import('@/views/admin/SuperAdminDashboard.vue'),
    meta: {
      requiresAuth: false,
      requiresAdmin: true,
      title: 'Analytics Dashboard - ProperFlight Admin',
    },
  },
  // Catch-all 404 page - must be the last route
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundPage.vue'),
    meta: {
      requiresAuth: false,
      titleKey: 'meta.notFound.title',
      descriptionKey: 'meta.notFound.description',
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    // Hash anchors (e.g. /#faq-section) scroll to the element
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    // Back/forward navigation restores position
    if (savedPosition) {
      return savedPosition;
    }
    // All other navigations go to the top
    return { top: 0, behavior: 'instant' };
  },
})

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()

  // ---------------------------------------------------------------------------
  // Activate locale from route param before any title/meta resolution
  // ---------------------------------------------------------------------------
  const routeLocale = to.params.locale
  const activeLocale = (routeLocale && SUPPORTED_LOCALES.includes(routeLocale))
    ? routeLocale
    : DEFAULT_LOCALE
  i18n.global.locale.value = activeLocale
  document.documentElement.lang = activeLocale

  // ---------------------------------------------------------------------------
  // Persist locale preference
  // ---------------------------------------------------------------------------
  try {
    localStorage.setItem('pfLocale', activeLocale)
  } catch (_) { /* storage unavailable */ }

  // Check if route requires superadmin authentication
  if (to.meta.requiresAdmin) {
    const adminStore = useAdminStore()
    if (!adminStore.isAdminAuthenticated) {
      next({ name: 'SuperAdminLogin' })
      return
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login or show auth modal
    authStore.openAuthModal()
    next('/')
    return
  }

  // ---------------------------------------------------------------------------
  // Resolve title and description — prefer i18n keys, fall back to hardcoded
  // ---------------------------------------------------------------------------
  const { t } = i18n.global
  const title = to.meta.titleKey
    ? t(to.meta.titleKey)
    : (to.meta.title || 'ProperFlight')
  const description = to.meta.descriptionKey
    ? t(to.meta.descriptionKey)
    : (to.meta.description || 'Find cheap flights that fit your schedule with ProperFlight.')

  // Update page title
  document.title = title

  // Update meta description and OG tags
  const url = `https://properflight.com${to.fullPath}`

  // Canonical: always points to the English (no-prefix) URL, and always to the
  // bare path.
  //
  // `to.path`, not `to.fullPath`: the query string must not reach the canonical.
  // No indexable route varies its content by query param — they only carry UI
  // state such as /explore?from=LON or /searches/basic?to=WAW — so a
  // self-referencing canonical on each parameter combination would publish an
  // unbounded set of near-duplicate URLs of the same page. Every variant now
  // canonicalises to the one prerendered URL that actually exists in dist/.
  const canonicalPath = to.path.replace(/^\/pl(\/|$)/, (_, sep) => sep || '/')
  const canonicalUrl = `https://properflight.com${canonicalPath}`
  let canonicalEl = document.querySelector('link[rel="canonical"]')
  if (!canonicalEl) {
    canonicalEl = document.createElement('link')
    canonicalEl.setAttribute('rel', 'canonical')
    document.head.appendChild(canonicalEl)
  }
  canonicalEl.setAttribute('href', canonicalUrl)

  // ---------------------------------------------------------------------------
  // Robots: keep private / transient pages out of the index. Auth-gated pages,
  // admin screens, search-result permalinks and the 404 page hold no search
  // value and would otherwise burn crawl budget.
  // ---------------------------------------------------------------------------
  const noindex = to.meta.noindex
    || to.meta.requiresAuth
    || to.meta.requiresAdmin
    || to.name === 'NotFound'
  let robotsEl = document.querySelector('meta[name="robots"]')
  if (!robotsEl) {
    robotsEl = document.createElement('meta')
    robotsEl.setAttribute('name', 'robots')
    document.head.appendChild(robotsEl)
  }
  robotsEl.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow')

  const image = to.meta.image || 'https://properflight.com/og-image.jpg'

  const metaUpdates = {
    'meta[name="description"]': { attr: 'name', name: 'description', content: description },
    'meta[property="og:title"]': { attr: 'property', name: 'og:title', content: title },
    'meta[property="og:description"]': { attr: 'property', name: 'og:description', content: description },
    'meta[property="og:url"]': { attr: 'property', name: 'og:url', content: url },
    'meta[property="og:image"]': { attr: 'property', name: 'og:image', content: image },
    'meta[name="twitter:title"]': { attr: 'name', name: 'twitter:title', content: title },
    'meta[name="twitter:description"]': { attr: 'name', name: 'twitter:description', content: description },
    'meta[name="twitter:image"]': { attr: 'name', name: 'twitter:image', content: image },
  }

  Object.entries(metaUpdates).forEach(([selector, { attr, name, content }]) => {
    let el = document.querySelector(selector)
    if (!el) {
      el = document.createElement('meta')
      el.setAttribute(attr, name)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  })

  next()
  
})

export default router
